import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import {
  AiOutlineArrowRight,
  AiFillClockCircle,
  AiFillPieChart,
} from "react-icons/ai";
import { GoPersonFill } from "react-icons/go";
import { IoLocation } from "react-icons/io5";
import { useNavigate, useParams } from "react-router";
import { ROOT_URL } from "../config";
import { server } from "../server";

type Brand = { attributes?: { name?: string | null } };

type SetMenuAttributes = {
  title?: string;
  description?: string;
  capacity?: number;
  date_str?: string;
  price?: number | string;
  brands?: Brand[];
  photo_title?: {
    data?: {
      attributes?: {
        url?: string;
        formats?: {
          small?: { url?: string };
        };
      };
    };
  };
  show_remaining_quantity?: boolean;
};

export type SetMenuItem = {
  id: number | string;
  countingSetMenu?: number;
  attributes?: SetMenuAttributes;
};

export type SetMenuCardProps = {
  item: SetMenuItem;
  counting?: number;
  haveInventory?: boolean;
  onReserve?: (item: SetMenuItem) => void;
  onShare?: (item: SetMenuItem) => void | Promise<void>;
  rootUrl?: string;
  reserveLabel?: string;
  soldOutLabel?: string;
};

const DEFAULT_ROOT = ROOT_URL || "";

const toAbsoluteUrl = (root: string, path?: string) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${root}${path}`;
};

// const APP_TEXT = `下載應用程式並立即登記！點擊以下連結下載：
// Apple App Store:
// https://apps.apple.com/hk/app/%E6%B5%B7%E6%B8%AF%E9%A3%B2%E9%A3%9F%E9%9B%86%E5%9C%98/id1250109928

// Google Play Store:
// https://play.google.com/store/apps/details?id=hk.com.hoikonghk.memberapp&pcampaignid=web_share`;

// async function defaultShare(menu: SetMenuAttributes, imageUrl: string) {
//     const title = `套餐: ${menu?.title ?? ""}`;
//     const text = `套餐: ${menu?.title ?? ""}
// 介紹: ${menu?.description ?? ""}
// ${APP_TEXT}`;

//     if (navigator.share) {
//         await navigator.share({
//             title,
//             text,
//             url: imageUrl || undefined,
//         });
//         return;
//     }

//     await navigator.clipboard.writeText(`${text}\n${imageUrl}`);
// }

export default function SetMenuCard({
  item,
  counting,
  haveInventory,
  onReserve,
  onShare,
  rootUrl = DEFAULT_ROOT,
  reserveLabel = "預訂",
  soldOutLabel = "已售罄",
}: SetMenuCardProps) {
  const setMenu = item?.attributes ?? {};
  const navigate = useNavigate();
  const { company: companyParam } = useParams();
  const brandsList = useMemo(
    () =>
      (setMenu?.brands ?? [])
        .map((v) => v?.attributes?.name)
        .filter(Boolean) as string[],
    [setMenu?.brands],
  );
  const [userSetMenu, setUserSetMenu] = useState<{
    counting: number;
    haveInventory: boolean;
  } | null>(null);
  const loadPageData = useCallback(
    async (signal?: AbortSignal) => {
      const res = await server.findAll("user-campaign-setmenus", {
        filters: {
          // user: { id: { $null: true } },
          email: { $null: true },
          phone: { $null: true },
          setmenu: item?.id,
        },
      });
      setUserSetMenu({
        counting: res?.length ?? 0,
        haveInventory: res?.length > 0,
      });
    },
    [item],
  );

  useEffect(() => {
    const controller = new AbortController();
    void loadPageData(controller.signal);
    return () => controller.abort();
  }, [loadPageData]);
  const canReserve = userSetMenu?.haveInventory;

  const smallPath = setMenu?.photo_title?.data?.attributes?.url;
  const imageUrl = toAbsoluteUrl(rootUrl, smallPath);

  return (
    <div
      style={{
        boxShadow: "0px 3px 4.65px rgba(20,20,20,0.29)",
        borderRadius: 20,
        padding: 15,
        margin: "5px 0",
        backgroundColor: "#FFFFFF",
      }}
    >
      {imageUrl ? (
        <img
          style={{ width: "100%", borderRadius: 8, objectFit: "cover" }}
          src={imageUrl}
          alt={setMenu?.title ?? "set menu"}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: 225,
            borderRadius: 8,
            backgroundColor: "#F2F2F2",
          }}
        />
      )}

      <div style={{ paddingTop: 15, paddingBottom: 15 }}>
        <div style={{ fontSize: 20, fontWeight: 500 }}>{setMenu?.title}</div>
      </div>

      {setMenu?.description ? (
        <div style={{ fontSize: 16, fontWeight: 400, color: "#7A7E80" }}>
          {setMenu?.description}
        </div>
      ) : null}

      {(setMenu?.capacity ?? 0) > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginTop: 10,
          }}
        >
          <GoPersonFill
            color={"var(--bs-primary)"}
            size={20}
            style={{ marginLeft: 1, marginRight: 10, marginTop: 2 }}
          />
          <div style={{ color: "#0E0F0F" }}>{`${setMenu?.capacity}人`}</div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "row" }}>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-end",
          }}
        >
          {brandsList.length > 0 ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                marginTop: 10,
                marginRight: 8,
              }}
            >
              <IoLocation
                color={"var(--bs-primary)"}
                size={20}
                style={{ marginRight: 10, marginTop: 2 }}
              />
              <div style={{ flex: 1, color: "#0E0F0F" }}>
                {brandsList.join("、")}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {setMenu?.date_str ? (
        <div style={{ display: "flex", flexDirection: "row", marginTop: 10 }}>
          <AiFillClockCircle
            color={"var(--bs-primary)"}
            size={20}
            style={{ marginLeft: 1, marginRight: 10, marginTop: 2 }}
          />
          <div style={{ color: "#0E0F0F" }}>{setMenu?.date_str}</div>
        </div>
      ) : null}

      {setMenu?.show_remaining_quantity ? (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginTop: 10,
          }}
        >
          <AiFillPieChart
            color={"var(--bs-primary)"}
            size={20}
            style={{ marginLeft: 1, marginRight: 10, marginTop: 2 }}
          />
          <div style={{ color: "#0E0F0F" }}>{`${userSetMenu?.counting}份`}</div>
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "row", margin: "16px 0" }}>
        <div
          style={{ color: "var(--bs-primary)", fontWeight: 400, fontSize: 18 }}
        >
          總價:
        </div>
        <div
          style={{
            color: "var(--bs-primary)",
            fontWeight: 800,
            fontSize: 18,
            marginInlineStart: 8,
          }}
        >{`$${setMenu?.price ?? ""}`}</div>
      </div>

      <div style={{ display: "flex", flexDirection: "row" }}>
        <Button
          type="button"
          disabled={!canReserve}
          style={{
            backgroundColor: !canReserve ? "gray" : "var(--bs-primary)",
            flex: 1,
            padding: "8px 0",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 50,
            overflow: "hidden",
            display: "flex",
            flexDirection: "row",
            border: "none",
            cursor: !canReserve ? "not-allowed" : "pointer",
          }}
          onClick={() => {
            navigate(`/${companyParam}/menusdetail`, {
              state: {
                menu: setMenu,
                setMenu: true,
                id: item?.id,
                counting: userSetMenu?.counting ?? 0,
              },
            });
          }}
        >
          {!canReserve ? (
            <div style={{ color: "white", fontSize: 20, fontWeight: 500 }}>
              {soldOutLabel}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
              }}
            >
              <div
                style={{
                  color: "white",
                  fontSize: 20,
                  fontWeight: 500,
                  textAlign: "center",
                  flex: 1,
                }}
              >
                {reserveLabel}
              </div>
              <AiOutlineArrowRight
                color="white"
                size={24}
                style={{ marginRight: 16 }}
              />
            </div>
          )}
        </Button>

        {/* <div style={{ display: "flex", justifyContent: "center", marginInlineStart: 16 }}>
          <button
            type="button"
            onClick={() => void handleShare()}
            style={{
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="share"
          >
            <img src="/assets/share.svg" alt="share" width={24} height={24} />
          </button>
        </div> */}
      </div>
    </div>
  );
}
